import type { 
  Invoice, 
  Estimate,
  ChangeOrder,
  MaterialOrder,
  PaymentRecord,
  LineItem,
  TaxRate,
  InvoiceTemplate,
  DocumentTemplate,
  EmailTemplate,
  RecurringInvoiceTemplate,
  BatchOperation,
  DashboardMetrics,
  ReportTemplate,
  PaginatedResponse,
  InvoiceFormData,
  PaymentFormData,
  InvoiceFilters,
  PaymentFilters,
  DocumentStatus
} from '../types/billing'
import { authService } from './auth'
import { apiClient } from './api'

const BASE_URL = '/api'

// Helper to build query strings
const buildQueryString = (params: Record<string, any>): string => {
  const query = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      query.append(key, String(value))
    }
  })
  return query.toString() ? `?${query.toString()}` : ''
}

// Generic API functions
async function apiFetch<T>(
  url: string, 
  options: RequestInit = {}
): Promise<T> {
  const method = (options.method || 'GET').toUpperCase()
  const body = typeof options.body === 'string' ? JSON.parse(options.body) : options.body
  try {
    const response = method === 'POST' ? await apiClient.post<T>(url, body)
      : method === 'PATCH' ? await apiClient.patch<T>(url, body)
      : method === 'PUT' ? await apiClient.put<T>(url, body)
      : method === 'DELETE' ? await apiClient.delete<T>(url)
      : await apiClient.get<T>(url)
    return response.data
  } catch (error: any) {
    // Preserve both the shared client's field errors and legacy detail readers.
    const data = error.response?.data || error.responseData || error.data || { detail: error.message }
    Object.assign(error, data)
    error.response = { ...error.response, data }
    throw error
  }
}

// Tax Rates
export const taxRatesApi = {
  list: () => apiFetch<PaginatedResponse<TaxRate>>(`${BASE_URL}/tax-rates/`),
  get: (id: number) => apiFetch<TaxRate>(`${BASE_URL}/tax-rates/${id}/`),
  create: (data: Partial<TaxRate>) => 
    apiFetch<TaxRate>(`${BASE_URL}/tax-rates/`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  update: (id: number, data: Partial<TaxRate>) =>
    apiFetch<TaxRate>(`${BASE_URL}/tax-rates/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  delete: (id: number) =>
    apiFetch<void>(`${BASE_URL}/tax-rates/${id}/`, { method: 'DELETE' }),
}

// Invoice Templates
export const invoiceTemplatesApi = {
  list: () => apiFetch<PaginatedResponse<InvoiceTemplate>>(`${BASE_URL}/invoice-templates/`),
  get: (id: number) => apiFetch<InvoiceTemplate>(`${BASE_URL}/invoice-templates/${id}/`),
  create: (data: Partial<InvoiceTemplate>) => 
    apiFetch<InvoiceTemplate>(`${BASE_URL}/invoice-templates/`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  update: (id: number, data: Partial<InvoiceTemplate>) =>
    apiFetch<InvoiceTemplate>(`${BASE_URL}/invoice-templates/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  setDefault: (id: number) =>
    apiFetch<InvoiceTemplate>(`${BASE_URL}/invoice-templates/${id}/set_default/`, {
      method: 'POST',
    }),
}

// Invoices
export const invoicesApi = {
  list: (filters?: InvoiceFilters) => 
    apiFetch<PaginatedResponse<Invoice>>(`${BASE_URL}/invoices/${buildQueryString(filters || {})}`),
  
  get: (id: number) => apiFetch<Invoice>(`${BASE_URL}/invoices/${id}/`),
  
  create: (data: InvoiceFormData) => 
    apiFetch<Invoice>(`${BASE_URL}/invoices/`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  update: (id: number, data: Partial<Invoice>) =>
    apiFetch<Invoice>(`${BASE_URL}/invoices/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  
  delete: (id: number) =>
    apiFetch<void>(`${BASE_URL}/invoices/${id}/`, { method: 'DELETE' }),
  
  // Special actions
  markSent: (id: number) =>
    apiFetch<Invoice>(`${BASE_URL}/invoices/${id}/mark_sent/`, { method: 'POST' }),
  
  sendEmail: (id: number, emailData?: { recipient_email?: string; subject?: string; body?: string }) =>
    apiFetch<{ success: boolean; message: string }>(`${BASE_URL}/invoices/${id}/send_invoice_email/`, {
      method: 'POST',
      body: JSON.stringify(emailData || {}),
    }),
  
  generatePdf: (id: number) =>
    apiFetch<{ pdf_url: string }>(`${BASE_URL}/invoices/${id}/generate_pdf/`, { method: 'POST' }),
  
  convertTo: (id: number, targetType: 'estimate' | 'work_order') =>
    apiFetch<{ success: boolean; new_document_id: number }>(`${BASE_URL}/invoices/${id}/convert_to/${targetType}/`, {
      method: 'POST',
    }),
  
  bulkUpdateStatus: (ids: number[], status: DocumentStatus) =>
    apiFetch<{ success: boolean; updated: number }>(`${BASE_URL}/invoices/bulk_update_status/`, {
      method: 'POST',
      body: JSON.stringify({ ids, status }),
    }),
  
  summary: (filters?: InvoiceFilters) =>
    apiFetch<{
      total_amount: string
      paid_amount: string
      outstanding_amount: string
      overdue_amount: string
      invoice_count: number
    }>(`${BASE_URL}/invoices/summary/${buildQueryString(filters || {})}`),
  
  paymentSummary: (id: number) =>
    apiFetch<{
      total: string
      paid: string
      balance: string
      payments: PaymentRecord[]
    }>(`${BASE_URL}/invoices/${id}/payment_summary/`),
}

// Estimates
export const estimatesApi = {
  list: (filters?: Record<string, any>) => 
    apiFetch<PaginatedResponse<Estimate>>(`${BASE_URL}/estimates/${buildQueryString(filters || {})}`),
  
  get: (id: number) => apiFetch<Estimate>(`${BASE_URL}/estimates/${id}/`),

  history: (id: number) => apiFetch<Array<{
    id: number
    event_type: string
    event_title: string
    event_description: string
    severity: string
    user: number | null
    timestamp: string
    event_data: Record<string, any>
  }>>(`${BASE_URL}/estimates/${id}/history/`),
  
  create: (data: Partial<Estimate>) => 
    apiFetch<Estimate>(`${BASE_URL}/estimates/`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  update: (id: number, data: Partial<Estimate>) =>
    apiFetch<Estimate>(`${BASE_URL}/estimates/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  
  accept: (id: number) =>
    apiFetch<Estimate>(`${BASE_URL}/estimates/${id}/accept/`, { method: 'POST' }),
  
  reject: (id: number, reason?: string) =>
    apiFetch<Estimate>(`${BASE_URL}/estimates/${id}/reject/`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),
  
  // `force` converts an estimate the customer has not formally accepted yet
  // (draft or sent). The server rejects those without it and answers with
  // requires_force so the caller can confirm.
  convertToInvoice: (id: number, force = false) =>
    apiFetch<{ status: string; invoice: Invoice; invoice_id?: number; suggest_material_order?: boolean; material_order_data?: any }>(`${BASE_URL}/estimates/${id}/convert_to_invoice/`, {
      method: 'POST',
      body: JSON.stringify({ force }),
    }),
  
  convertToJob: (id: number) =>
    apiFetch<{ success: boolean; job_id: number }>(`${BASE_URL}/estimates/${id}/convert_to_job/`, {
      method: 'POST',
    }),
  
  delete: (id: number) =>
    apiFetch<void>(`${BASE_URL}/estimates/${id}/`, { method: 'DELETE' }),
  
  sendEmail: (id: number, emailData: { template_id: number; recipient_email: string; recipient_name?: string; include_pdf?: boolean; custom_subject?: string; custom_text_content?: string }) =>
    apiFetch<{ success: boolean; message: string }>(`${BASE_URL}/estimates/${id}/send_email/`, {
      method: 'POST',
      body: JSON.stringify(emailData),
    }),

  previewEmail: (id: number, template_id: number) =>
    apiFetch<{ subject: string; text_content: string }>(`${BASE_URL}/estimates/${id}/preview-email/`, {
      method: 'POST',
      body: JSON.stringify({ template_id }),
    }),
  
  generatePdf: (id: number) =>
    apiFetch<{ file_url: string }>(`${BASE_URL}/estimates/${id}/pdf/`, { method: 'POST' }),
  
  bulkUpdateStatus: (ids: number[], status: DocumentStatus) =>
    apiFetch<{ success: boolean; updated: number }>(`${BASE_URL}/estimates/bulk_update_status/`, {
      method: 'POST',
      body: JSON.stringify({ estimate_ids: ids, status }),
    }),
  
  summary: (filters?: Record<string, any>) =>
    apiFetch<{
      total_amount: string
      accepted_amount: string
      pending_amount: string
      expired_amount: string
      estimate_count: number
      accepted_count: number
      pending_count: number
      expired_count: number
    }>(`${BASE_URL}/estimates/summary/${buildQueryString(filters || {})}`),
}

// Work Orders
// WorkOrder API removed - using subcontractors app WorkOrder instead
// See apiSubcontractors.ts for work order functionality

// Email Templates
export const emailTemplatesApi = {
  list: (filters?: Record<string, any>) => 
    apiFetch<PaginatedResponse<EmailTemplate>>(`${BASE_URL}/email-templates/${buildQueryString(filters || {})}`),
  
  get: (id: number) => apiFetch<EmailTemplate>(`${BASE_URL}/email-templates/${id}/`),
}

// Change Orders
export const changeOrdersApi = {
  list: (filters?: Record<string, any>) => 
    apiFetch<PaginatedResponse<ChangeOrder>>(`${BASE_URL}/change-orders/${buildQueryString(filters || {})}`),
  
  get: (id: number) => apiFetch<ChangeOrder>(`${BASE_URL}/change-orders/${id}/`),
  
  create: (data: Partial<ChangeOrder>) => 
    apiFetch<ChangeOrder>(`${BASE_URL}/change-orders/`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  approve: (id: number, approvedBy: string) =>
    apiFetch<ChangeOrder>(`${BASE_URL}/change-orders/${id}/approve/`, {
      method: 'POST',
      body: JSON.stringify({ approved_by: approvedBy }),
    }),
  
  reject: (id: number) =>
    apiFetch<ChangeOrder>(`${BASE_URL}/change-orders/${id}/reject/`, { method: 'POST' }),
}

// Material Orders
export const materialOrdersApi = {
  list: (filters?: Record<string, any>) => 
    apiFetch<PaginatedResponse<MaterialOrder>>(`${BASE_URL}/material-orders/${buildQueryString(filters || {})}`),
  
  get: (id: number) => apiFetch<MaterialOrder>(`${BASE_URL}/material-orders/${id}/`),
  
  create: (data: Partial<MaterialOrder>) => 
    apiFetch<MaterialOrder>(`${BASE_URL}/material-orders/`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  update: (id: number, data: Partial<MaterialOrder>) =>
    apiFetch<MaterialOrder>(`${BASE_URL}/material-orders/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  
  updateDeliveryStatus: (id: number, status: string, trackingNumber?: string) =>
    apiFetch<MaterialOrder>(`${BASE_URL}/material-orders/${id}/update_delivery/`, {
      method: 'POST',
      body: JSON.stringify({ status, tracking_number: trackingNumber }),
    }),
}

// Line Items
export const lineItemsApi = {
  list: (documentType: string, documentId: number) => 
    apiFetch<LineItem[]>(`${BASE_URL}/line-items/?document_type=${documentType}&document_id=${documentId}`),
  
  create: (data: Partial<LineItem>) => 
    apiFetch<LineItem>(`${BASE_URL}/line-items/`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  update: (id: number, data: Partial<LineItem>) =>
    apiFetch<LineItem>(`${BASE_URL}/line-items/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  
  delete: (id: number) =>
    apiFetch<void>(`${BASE_URL}/line-items/${id}/`, { method: 'DELETE' }),
  
  bulkCreate: (items: Partial<LineItem>[]) =>
    apiFetch<LineItem[]>(`${BASE_URL}/line-items/bulk_create/`, {
      method: 'POST',
      body: JSON.stringify({ items }),
    }),
  
  reorder: (documentType: string, documentId: number, itemIds: number[]) =>
    apiFetch<{ success: boolean }>(`${BASE_URL}/line-items/reorder/`, {
      method: 'POST',
      body: JSON.stringify({ document_type: documentType, document_id: documentId, item_ids: itemIds }),
    }),
}

// Raw fetch for binary (PDF) downloads — apiFetch only handles JSON responses.
async function downloadPdfBlob(path: string): Promise<Blob> {
  const doFetch = (token?: string | null) =>
    fetch(`${BASE_URL}${path}`, {
      method: 'GET',
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    })
  let response = await doFetch(authService.getAccessToken())
  if (response.status === 401) {
    const newToken = await authService.refreshAccessToken()
    response = await doFetch(newToken)
  }
  if (!response.ok) {
    throw new Error(`Failed to download PDF (${response.status})`)
  }
  return response.blob()
}

// Payments
export const paymentsApi = {
  totals: (filters?: PaymentFilters) => apiFetch<{count:number;net_received:string}>(`${BASE_URL}/payments/totals/${buildQueryString(filters || {})}`),
  list: (filters?: PaymentFilters) => 
    apiFetch<PaginatedResponse<PaymentRecord>>(`${BASE_URL}/payments/${buildQueryString(filters || {})}`),
  
  get: (id: number) => apiFetch<PaymentRecord>(`${BASE_URL}/payments/${id}/`),
  
  create: (data: PaymentFormData) => 
    apiFetch<PaymentRecord>(`${BASE_URL}/payments/`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  update: (id: number, data: Partial<PaymentRecord>) =>
    apiFetch<PaymentRecord>(`${BASE_URL}/payments/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  
  delete: (id: number) =>
    apiFetch<void>(`${BASE_URL}/payments/${id}/`, { method: 'DELETE' }),
  
  // Download a PDF receipt for a payment.
  downloadReceipt: (id: number) => downloadPdfBlob(`/payments/${id}/receipt/`),

  // Default email draft (recipient/subject/message) to preview before sending.
  getReceiptDraft: (id: number) =>
    apiFetch<{ to_email: string; subject: string; message: string }>(
      `${BASE_URL}/payments/${id}/send_receipt/`
    ),

  // Email the receipt to the customer with the (possibly edited) message.
  sendReceipt: (id: number, data: { to_email: string; subject: string; message: string }) =>
    apiFetch<{ success: boolean; message: string }>(`${BASE_URL}/payments/${id}/send_receipt/`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
}

// Invoice / estimate email. Same preview-then-send shape as receipts, and
// needs no EmailTemplate: the draft wording comes from Billing Settings.
const EMAIL_PATHS: Record<'invoice' | 'estimate', string> = {
  invoice: 'invoices',
  estimate: 'estimates',
}

export const documentEmailApi = {
  getDraft: (kind: 'invoice' | 'estimate', id: number) =>
    apiFetch<{ to_email: string; subject: string; message: string; document_number: string }>(
      `${BASE_URL}/${EMAIL_PATHS[kind]}/${id}/send_document/`
    ),

  send: (
    kind: 'invoice' | 'estimate',
    id: number,
    data: { to_email: string; subject: string; message: string }
  ) =>
    apiFetch<{ success: boolean; message: string }>(
      `${BASE_URL}/${EMAIL_PATHS[kind]}/${id}/send_document/`,
      { method: 'POST', body: JSON.stringify(data) }
    ),
}

// Recurring Invoices
export const recurringInvoicesApi = {
  list: (filters?: {page?: number; page_size?: number; search?: string; queue?: string; status?: string}) =>
    apiFetch<PaginatedResponse<RecurringInvoiceTemplate>>(`${BASE_URL}/recurring-templates/${buildQueryString(filters || {})}`),
  
  get: (id: number) => apiFetch<RecurringInvoiceTemplate>(`${BASE_URL}/recurring-templates/${id}/`),
  
  create: (data: Partial<RecurringInvoiceTemplate>) => 
    apiFetch<RecurringInvoiceTemplate>(`${BASE_URL}/recurring-templates/`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  update: (id: number, data: Partial<RecurringInvoiceTemplate>) =>
    apiFetch<RecurringInvoiceTemplate>(`${BASE_URL}/recurring-templates/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  
  activate: (id: number) =>
    apiFetch<RecurringInvoiceTemplate>(`${BASE_URL}/recurring-templates/${id}/activate/`, {
      method: 'POST',
    }),
  
  deactivate: (id: number) =>
    apiFetch<RecurringInvoiceTemplate>(`${BASE_URL}/recurring-templates/${id}/deactivate/`, {
      method: 'POST',
    }),
  
  preview: (id: number) =>
    apiFetch<Invoice>(`${BASE_URL}/recurring-templates/${id}/preview/`),
  
  runNow: (id: number) =>
    apiFetch<{ success: boolean; invoice_id: number }>(`${BASE_URL}/recurring-templates/${id}/run_now/`, {
      method: 'POST',
    }),
}

// Document Templates
export const documentTemplatesApi = {
  // The filter is `template_type` — `document_type` was ignored by DRF, so
  // asking for invoice templates returned every template the org had.
  list: (templateType?: string) =>
    apiFetch<PaginatedResponse<DocumentTemplate>>(
      `${BASE_URL}/document-templates/${buildQueryString({ template_type: templateType })}`
    ),
  
  get: (id: number) => apiFetch<DocumentTemplate>(`${BASE_URL}/document-templates/${id}/`),
  
  create: (data: Partial<DocumentTemplate>) => 
    apiFetch<DocumentTemplate>(`${BASE_URL}/document-templates/`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  update: (id: number, data: Partial<DocumentTemplate>) =>
    apiFetch<DocumentTemplate>(`${BASE_URL}/document-templates/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  
  preview: (id: number, sampleData: Record<string, any>) =>
    apiFetch<{ html: string }>(`${BASE_URL}/document-templates/${id}/preview/`, {
      method: 'POST',
      body: JSON.stringify(sampleData),
    }),
}

// The emailTemplatesApi is already defined earlier in the file

// Batch Operations
export const batchOperationsApi = {
  list: () => 
    apiFetch<PaginatedResponse<BatchOperation>>(`${BASE_URL}/batch-operations/`),
  
  get: (id: number) => apiFetch<BatchOperation>(`${BASE_URL}/batch-operations/${id}/`),
  
  create: (operationType: string, items: any[]) => 
    apiFetch<BatchOperation>(`${BASE_URL}/batch-operations/`, {
      method: 'POST',
      body: JSON.stringify({ operation_type: operationType, items }),
    }),
  
  getStatus: (id: number) =>
    apiFetch<BatchOperation>(`${BASE_URL}/batch-operations/${id}/status/`),
  
  cancel: (id: number) =>
    apiFetch<BatchOperation>(`${BASE_URL}/batch-operations/${id}/cancel/`, {
      method: 'POST',
    }),
}

// Dashboard & Analytics
export const analyticsApi = {
  dashboard: () => 
    apiFetch<DashboardMetrics>(`${BASE_URL}/analytics/dashboard/`),
  
  revenueReport: (filters: { date_from: string; date_to: string; grouping?: string }) =>
    apiFetch<any>(`${BASE_URL}/analytics/revenue/${buildQueryString(filters)}`),
  
  agingReport: (asOf?: string) =>
    apiFetch<any>(`${BASE_URL}/analytics/aging/${asOf ? `?as_of=${asOf}` : ''}`),
  
  customerReport: (customerId: number, dateFrom?: string, dateTo?: string) =>
    apiFetch<any>(`${BASE_URL}/analytics/customer/${customerId}/${buildQueryString({ date_from: dateFrom, date_to: dateTo })}`),
  
  profitabilityReport: (filters: { date_from: string; date_to: string; job_id?: number }) =>
    apiFetch<any>(`${BASE_URL}/analytics/profitability/${buildQueryString(filters)}`),
  
  taxReport: (year: number, quarter?: number) =>
    apiFetch<any>(`${BASE_URL}/analytics/tax/${buildQueryString({ year, quarter })}`),
}

// Report Templates
export const reportTemplatesApi = {
  list: () => 
    apiFetch<PaginatedResponse<ReportTemplate>>(`${BASE_URL}/report-templates/`),
  
  get: (id: number) => apiFetch<ReportTemplate>(`${BASE_URL}/report-templates/${id}/`),
  
  create: (data: Partial<ReportTemplate>) => 
    apiFetch<ReportTemplate>(`${BASE_URL}/report-templates/`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  
  update: (id: number, data: Partial<ReportTemplate>) =>
    apiFetch<ReportTemplate>(`${BASE_URL}/report-templates/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),
  
  run: (id: number, parameters?: Record<string, any>) =>
    apiFetch<any>(`${BASE_URL}/report-templates/${id}/run/`, {
      method: 'POST',
      body: JSON.stringify(parameters || {}),
    }),
  
  export: (id: number, format: 'csv' | 'excel' | 'pdf', parameters?: Record<string, any>) => {
    const accessToken = authService.getAccessToken();
    return fetch(`${BASE_URL}/report-templates/${id}/export/`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        ...(accessToken && { 'Authorization': `Bearer ${accessToken}` }),
      },
      body: JSON.stringify({ format, ...parameters }),
    }).then(response => {
      if (!response.ok) throw new Error('Export failed')
      return response.blob()
    })
  },
}